import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    name:    'Bapak Hendra S.',
    role:    'Direktur PT Mandiri Jaya',
    avatar:  'H',
    rating:  5,
    text:    'Pelayanan sangat profesional dan membantu penyelesaian masalah perusahaan kami. Tim mereka benar-benar kompeten dan responsif dalam setiap tahap proses hukum.',
    color:   'from-blue-500 to-blue-700',
  },
  {
    name:    'Ibu Sari Wulandari',
    role:    'Pemilik Properti & Investor',
    avatar:  'S',
    rating:  5,
    text:    'Proses konsultasi cepat dan jelas. Masalah sengketa tanah keluarga kami yang sudah bertahun-tahun berhasil diselesaikan dengan sangat baik. Sangat puas!',
    color:   'from-gold-500 to-gold-700',
  },
  {
    name:    'Bapak Ricky Pratama',
    role:    'CEO Startup Teknologi',
    avatar:  'R',
    rating:  5,
    text:    'Sangat direkomendasikan untuk kebutuhan hukum bisnis. Kontrak perusahaan kami disusun dengan sangat cermat dan melindungi kepentingan kami sepenuhnya.',
    color:   'from-emerald-500 to-emerald-700',
  },
  {
    name:    'Ibu Dewi Anggraini',
    role:    'Pengusaha Retail',
    avatar:  'D',
    rating:  5,
    text:    'Penanganan kasus yang cepat dan profesional. Saya sangat terbantu dengan penjelasan yang mudah dipahami tentang proses hukum yang rumit.',
    color:   'from-purple-500 to-purple-700',
  },
];

function Stars({ count }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} size={14} className="text-gold-400 fill-gold-400" />
      ))}
    </div>
  );
}

const cardVariants = {
  hidden:  { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Testimonials() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section id="testimoni" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-600/30 to-transparent" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(ellipse 60% 50% at 50% 50%, rgba(212,149,14,0.04) 0%, transparent 70%)`,
        }}
      />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-gold-400 text-sm font-semibold tracking-widest uppercase mb-3">
            Testimoni Klien
          </span>
          <h2 className="section-title mb-4">
            Apa Kata{' '}
            <span className="gold-text">Klien Kami</span>
          </h2>
          <div className="divider-gold mx-auto mb-5" />
          <p className="section-subtitle max-w-2xl mx-auto">
            Kepercayaan dan kepuasan klien adalah tolok ukur keberhasilan kami
            dalam setiap penanganan perkara hukum.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              whileHover={{ y: -6, transition: { duration: 0.25 } }}
              className="group glass rounded-2xl p-6 border border-white/5 hover:border-gold-600/20 transition-all duration-400 flex flex-col"
            >
              {/* Quote icon */}
              <Quote size={32} className="text-gold-600/30 mb-4 flex-shrink-0" fill="currentColor" />

              {/* Text */}
              <p className="text-navy-200 text-sm leading-relaxed mb-6 flex-1">"{t.text}"</p>

              {/* Rating */}
              <Stars count={t.rating} />

              {/* Divider */}
              <div className="h-px bg-white/5 my-4" />

              {/* Avatar + Name */}
              <div className="flex items-center gap-3">
                <div
                  className={`w-11 h-11 rounded-full bg-gradient-to-br ${t.color} flex items-center justify-center font-bold text-white text-sm flex-shrink-0 shadow-lg`}
                >
                  {t.avatar}
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{t.name}</p>
                  <p className="text-navy-400 text-xs">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
